"use client";

import React, { useEffect, useRef, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import Section from "@/components/common/Section/Section";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

import { cleanUpMap, renderMap } from "./partnerMap";
import "./storelocator.css";

export type PartnerLocatorData = {
  heading: string;
  references: string;
};

type PartnerLocatorProps = {
  data: PartnerLocatorData;
};

export default function PartnerLocator({ data }: PartnerLocatorProps) {
  const widgetRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] = useState<"list" | "map">("list");

  useEffect(() => {
    renderMap();
    let watch: MutationObserver | undefined;
    const timer = window.setTimeout(() => {
      const canvas = document.getElementById("storelocator-map_canvas");
      if (!canvas) return;
      watch = new MutationObserver(() => {
        const style = window.getComputedStyle(canvas);
        if (style.zIndex === "1") setCurrentView("map");
      });
      watch.observe(canvas, { attributes: true, attributeFilter: ["style"] });
    }, 5000);
    return () => {
      window.clearTimeout(timer);
      watch?.disconnect();
      cleanUpMap();
    };
  }, []);

  useEffect(() => {
    const root = widgetRef.current;
    if (!root) return;
    const listBtn = root.querySelector<HTMLElement>("#storelocator-showstorelistbtn");
    const mapBtn = root.querySelector<HTMLElement>("#storelocator-showmapbtn");
    if (currentView === "map") mapBtn?.click();
    if (currentView === "list") listBtn?.click();
  }, [currentView]);

  return (
    <Section maxWidth={1520} px="px-6" py="py-0">
      <TextElementNova
        element="h3"
        text={data.heading}
        className="Title-20-Super-Normal text-center"
      />
      <div className="mt-10 md:mt-16">
        <div id="map-script"></div>
        <div className="flex flex-col flex-wrap text-base md:h-14 md:flex-row md:text-sm">
          <div id="storelocator-search" className="mb-2 flex w-full md:mb-0 md:mr-2"></div>
          <div
            id="storelocator-location"
            className="flex min-h-[88px] w-full flex-col md:min-h-0 md:flex-row"
          >
            <div id="storelocator-distance-container" className="flex min-h-14 flex-1">
              {/* Widget moves #storelocator-nearby and the radius select into this row. */}
            </div>
            <div
              id="storelocator-results-wrapper"
              className="flex flex-1 md:items-end md:justify-end"
            >
              <span id="storelocator-results" className="mt-2.5 md:ml-auto">
                Showing 0 results
              </span>
            </div>
          </div>
          <div className="mt-[5px] mb-3 flex items-center justify-between rounded-lg bg-nova-base-solid-20 p-4 min-[600px]:hidden">
            <TextElementNova
              element="span"
              text="Select View"
              className="Body-20-Dia-Strong text-nova-brand-primary-80"
            />
            <div className="flex items-center">
              <TextElementNova
                element="span"
                text="Map"
                className={cn("Body-20-Dia-Normal mr-2 text-nova-brand-primary-80", {
                  "opacity-40": currentView !== "map",
                })}
              />
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                onClick={() => setCurrentView(currentView === "list" ? "map" : "list")}
                role="button"
                aria-label="Toggle map and list"
              >
                <rect width="40" height="24" rx="12" fill="#008078" />
                <rect
                  x={currentView === "list" ? 20 : 4}
                  y="4"
                  width="16"
                  height="16"
                  rx="8"
                  fill="white"
                />
              </svg>
              <TextElementNova
                element="span"
                text="List"
                className={cn("Body-20-Dia-Normal ml-2 text-nova-brand-primary-80", {
                  "opacity-40": currentView !== "list",
                })}
              />
            </div>
          </div>
        </div>
        <div id="storelocatorwidget" className="storelocator-container mb-4" ref={widgetRef}></div>
      </div>
      <p className="Caption-10-Dia-Normal mt-8 rounded border border-nova-transparency-black-20 p-6 text-center md:px-20 md:py-10">
        Are you a healthcare provider? Learn about offering Nutrafol in your practice and online{" "}
        <LinkHandler
          path="/hair/benefits/services/professionals/become-a-partner/"
          className="text-nova-brand-primary-80 underline hover:text-nova-brand-primary-100"
        >
          here
        </LinkHandler>
        . If you&apos;re a hair professional, email us at{" "}
        <a
          href="mailto:partners@nutrafol.com"
          className="text-nova-brand-primary-80 underline hover:text-nova-brand-primary-100"
          aria-label="Email partners@nutrafol.com"
        >
          partners@nutrafol.com
        </a>{" "}
        to learn about becoming a partnered salon.
        <br />
        <br />
        Nutrafol Certified Partners are designated with a yellow badge. These partners have
        successfully completed training on the Nutrafol whole-body approach, and our products.
      </p>
      <TextElementNova
        element="p"
        text={data.references}
        className="Caption-10-Dia-Normal pb-4 pt-8 text-center text-nova-transparency-black-60 md:pb-6 md:pt-10"
      />
    </Section>
  );
}
